import React, { Component } from 'react';
import { Text } from '@components-library/Typography';
import { EditableHTMLModal } from '@components-shared/configConstructor/EditableHTMLModal';
import { keyMap } from '@locales/lokalise/keyMap';
import { fetchApi } from 'api/fetchApi';
import { handleError } from 'utils/errors';
import DatetimePicker from './datetimepicker';
import FileSourceInput from './fileSourceInput';
import { Select } from './select';
import { Attributes, EventTarget, INPUT_TABLE_TYPE_FIELD, InputsTableValues, Option } from './types';
import { mapType } from './utils';

const { BOOL, DYNAMIC_DROPDOWN, DECIMAL, ENUM, HTML, EDITABLE_HTML, DATETIME, FILE_SOURCE } = INPUT_TABLE_TYPE_FIELD;

const PREFFIX = 'cc-table';

interface IProps {
  members: Option;
  value: Array<InputsTableValues>;
  name: string;
  onChange: (e: EventTarget) => void;
  getLabel: (attributes: Attributes, key: string) => string;
  onSetChange?: () => void;
  disabled: boolean;
  attributes: Attributes;
  classNamePreffix?: string;
}

interface IState {
  data: Map<string, Array<{ name: string; key: number }>>;
}

interface DefaultInputProps {
  attributes: Attributes;
  name: string;
  required: boolean;
  onChange: (e: EventTarget) => void;
  onSetChange: () => void;
}

interface StringValueComponentProps extends DefaultInputProps {
  value: string;
}

interface SelectProps extends DefaultInputProps {
  value: string | null;
}

class InputsTable extends Component<IProps, IState> {
  state = {
    data: new Map(),
  };

  componentDidMount(): void {
    const { members } = this.props;

    const dynamicDropdowns = members.value?.members.filter(
      (x) => x.type.toLowerCase() === DYNAMIC_DROPDOWN,
    ) as Array<Option>;

    dynamicDropdowns.forEach((x) =>
      this.getOptions(x.attributes?.dynamicDroppdownSetting?.remoteUrl as string, x.name),
    );
  }

  renderInput(member: Option, val: InputsTableValues, index: number): JSX.Element {
    const { value, name, onChange, getLabel, onSetChange, disabled, members } = this.props;

    const inputProps = {
      value: val[member.name],
      attributes: { ...member.attributes, disabled },
      name: member.name,
      required: !member.canBeNull,
      onChange: (e: EventTarget) => {
        const newValue = [...value];
        const checkboxValue = e.target.checked ?? false;

        newValue[index][member.name] = member.type === 'bool' ? checkboxValue : (e.target.value as string | boolean);

        onChange({
          target: { name, value: newValue, type: member.type },
        });
      },

      onChangeHtmlEditor: (editorValue: string) => {
        const newValue = [...value];

        newValue[index][member.name] = editorValue;

        onChange({
          target: { name, value: newValue, type: member.type },
        });
      },
      onSetChange,
    };

    const type = member.type?.toLowerCase();
    const isArrayMembersType = members?.type === 'array';

    switch (type) {
      case DATETIME: {
        return <DatetimePicker {...(inputProps as StringValueComponentProps)} />;
      }
      case EDITABLE_HTML:
        return (
          <EditableHTMLModal
            onChange={inputProps.onChangeHtmlEditor}
            value={inputProps.value as string}
            title={inputProps.name}
          />
        );
      case BOOL: {
        const checkOnValue = () => (val[member.name] ? true : false);
        const isChecked = checkOnValue();

        return (
          <div className="cc-field__checkbox-container">
            <label className="cc-field bool ">
              <input
                type="checkbox"
                checked={isChecked}
                {...(inputProps as StringValueComponentProps)}
                {...inputProps.attributes}
              />
              <span className={`cc-filed__checkbox  ${val[member.name]}`} />
            </label>
          </div>
        );
      }
      case ENUM: {
        return (
          <Select
            {...(inputProps as SelectProps)}
            {...inputProps.attributes}
            options={member.values}
            getLabel={getLabel}
          />
        );
      }
      case DYNAMIC_DROPDOWN: {
        const { data } = this.state;
        const { name } = member;
        const options = data.get(name);

        if (!options) {
          inputProps.attributes.disabled = true;
        }

        return (
          <Select {...(inputProps as SelectProps)} {...inputProps.attributes} options={options} getLabel={getLabel} />
        );
      }
      case HTML: {
        return <div className="cc-field html" dangerouslySetInnerHTML={{ __html: val[member.name] as string }} />;
      }
      case FILE_SOURCE: {
        const { name, value, attributes, onChange, required } = inputProps;

        return (
          <FileSourceInput
            className="cc-input"
            classNameССInput="border--disable"
            key={name}
            name={name}
            value={value as string}
            attributes={attributes}
            onChange={onChange}
            required={required}
            disabled={disabled}
            hideElement={isArrayMembersType}
          />
        );
      }
      default: {
        const isInputValueDecimal = type === DECIMAL;
        const stepForInputDecimalValue = isInputValueDecimal ? 'any' : undefined;

        return (
          <input
            type={mapType(member.type)}
            {...(inputProps as StringValueComponentProps)}
            {...inputProps.attributes}
            step={stepForInputDecimalValue}
          />
        );
      }
    }
  }

  validate = () => {
    const { value, members } = this.props;
    const notCanbeNullFields = members.value?.members
      .filter((m) => !m.canBeNull && m.type !== 'bool')
      .map((m) => m.name) as Array<string>;

    return !value.length || value.every((v) => notCanbeNullFields.every((f) => v[f] !== ''));
  };

  async getOptions(remoteUrl: string, name: string) {
    const dataMap = this.state.data;

    try {
      const response = await fetchApi.post<Array<Option>>(remoteUrl);
      const options = response.data.map((o) => ({ name: o.name, key: o.id }));

      dataMap.set(name, options);

      this.setState({ data: dataMap });
    } catch (error) {
      handleError(`Failed to fetch options from ${remoteUrl}`, error);
    }
  }

  render() {
    const { members, value, name, onChange, getLabel, disabled, attributes, classNamePreffix = PREFFIX } = this.props;
    const { tableExcludedAction: disabledAction = [] } = attributes;
    const isDisableAction = (action: string) => disabledAction.indexOf(action) > -1;

    return (
      <div className={`${classNamePreffix}__container`}>
        <table className={classNamePreffix}>
          <thead className={`${classNamePreffix}__thead`}>
            <tr>
              {members.value?.members.map((m) => (
                <th className={`${classNamePreffix}__th`} key={m.name}>
                  {getLabel(m.attributes, m.name)}
                </th>
              ))}
              {!disabled && !isDisableAction('Delete') && (
                <th className={`${classNamePreffix}__th`}>
                  <Text>{keyMap.sharedActionsActions}</Text>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {value.map((v, i) => {
              const id = i;

              return (
                <tr key={id}>
                  {members.value?.members.map((m) => (
                    <td className={`${classNamePreffix}__td`} key={m.name}>
                      {this.renderInput(m, v, i)}
                    </td>
                  ))}
                  {!disabled && !isDisableAction('Delete') && (
                    <td className={`${classNamePreffix}__td`}>
                      <button
                        type="button"
                        onClick={() => {
                          value.splice(i, 1);
                          onChange({
                            target: { name, value, type: members.type },
                          });
                        }}>
                        <Text>{keyMap.sharedActionsDelete}</Text>
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {!disabled && !isDisableAction('Add') && (
              <tr>
                <td className={`${classNamePreffix}__td`} colSpan={(members.value?.members.length as number) + 1}>
                  <button
                    type="button"
                    onClick={() => {
                      value.push(
                        members.value?.members.reduce(
                          (aggr, m) => ({ ...aggr, [m.name]: m.type === 'bool' ? false : '' }),
                          {},
                        ) as InputsTableValues,
                      );
                      onChange({
                        target: { name, value, type: members.type },
                      });
                    }}>
                    <Text>{keyMap.sharedActionsAdd}</Text>
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }
}

export default InputsTable;
